import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import useAuthStore from '../store/authStore';

interface ScheduleEvent {
  id: number;
  title: string;
  subject: string | null;
  startTime: string;
  endTime: string;
  repeat: string;
  color: string | null;
}

const EVENT_COLORS = ['#f87171', '#fb923c', '#facc15', '#4ade80', '#60a5fa', '#c084fc', '#f472b6'];

const Schedule: React.FC<{ kidId?: number | string }> = ({ kidId }) => {
  const { userId } = useAuthStore();
  const effectiveKidId = kidId ?? userId;
  const [events, setEvents] = useState<ScheduleEvent[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showForm, setShowForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [form, setForm] = useState({
    title: '', subject: '', startTime: '', endTime: '',
    repeat: 'NONE', color: '#60a5fa'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const res = await api.get(`/api/kids/${effectiveKidId}/events`);
      setEvents(res.data);
    } catch {}
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/api/kids/${effectiveKidId}/events/${id}`);
      fetchEvents();
    } catch {}
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/api/kids/${effectiveKidId}/events`, {
        ...form,
        startTime: form.startTime,
        endTime: form.endTime,
      });
      setShowForm(false);
      setForm({ title: '', subject: '', startTime: '', endTime: '', repeat: 'NONE', color: '#60a5fa' });
      fetchEvents();
    } catch {
      alert('Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  // Calendar helpers
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const getEventsForDay = (day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.startTime.startsWith(dateStr));
  };

  const handleDayClick = (day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
    setForm(f => ({
      ...f,
      startTime: `${dateStr}T09:00`,
      endTime: `${dateStr}T10:00`,
    }));
    setShowForm(true);
  };

  const today = new Date();
  const isToday = (day: number) =>
    day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  // Events for selected date (for sidebar)
  const selectedEvents = selectedDate
    ? events.filter(e => e.startTime.startsWith(selectedDate))
    : [];

  return (
    <div>
      <main className="space-y-4">
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-display font-extrabold text-gray-800">📅 Schedule</h1>
          <button onClick={() => setShowForm(!showForm)}
            className="bg-pink-600 text-white text-sm px-3 py-1.5 rounded-lg">+ Add</button>
        </div>
        {/* Add Event Form */}
        {showForm && (
          <div className="bg-white rounded-2xl shadow p-5">
            <h3 className="font-semibold text-gray-700 mb-3">
              Add Event {selectedDate && <span className="text-sm text-gray-400">— {selectedDate}</span>}
            </h3>
            <form onSubmit={handleAdd} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-sm text-gray-600">Title</label>
                  <input type="text" required value={form.title}
                    onChange={e => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g. Math tutoring"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400" />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Start</label>
                  <input type="datetime-local" required value={form.startTime}
                    onChange={e => setForm({ ...form, startTime: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400" />
                </div>
                <div>
                  <label className="text-sm text-gray-600">End</label>
                  <input type="datetime-local" required value={form.endTime}
                    onChange={e => setForm({ ...form, endTime: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400" />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Subject (optional)</label>
                  <input type="text" value={form.subject}
                    onChange={e => setForm({ ...form, subject: e.target.value })}
                    placeholder="e.g. Math"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400" />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Repeat</label>
                  <select value={form.repeat} onChange={e => setForm({ ...form, repeat: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400">
                    <option value="NONE">No repeat</option>
                    <option value="DAILY">Daily</option>
                    <option value="WEEKLY">Weekly</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Color</label>
                  <div className="flex gap-2 mt-1">
                    {EVENT_COLORS.map(c => (
                      <button key={c} type="button"
                        onClick={() => setForm({ ...form, color: c })}
                        className={`w-6 h-6 rounded-full border-2 ${form.color === c ? 'border-gray-700 scale-125' : 'border-transparent'}`}
                        style={{ backgroundColor: c }} />
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowForm(false)} className="text-sm text-gray-500 px-4 py-2">Cancel</button>
                <button type="submit" disabled={saving}
                  className="bg-pink-600 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Calendar */}
        <div className="bg-white rounded-2xl shadow overflow-hidden">
          {/* Month navigation */}
          <div className="flex items-center justify-between px-5 py-3 bg-pink-600 text-white">
            <button onClick={() => setCurrentDate(new Date(year, month - 1, 1))} className="text-lg font-bold">‹</button>
            <h2 className="font-semibold">{monthName}</h2>
            <button onClick={() => setCurrentDate(new Date(year, month + 1, 1))} className="text-lg font-bold">›</button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 bg-pink-50">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} className="text-center text-xs font-semibold text-gray-500 py-2">{d}</div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 border-t border-gray-100">
            {/* Empty cells before first day */}
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} className="border-b border-r border-gray-100 h-20" />
            ))}

            {/* Day cells */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dayEvents = getEventsForDay(day);
              const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              return (
                <div
                  key={day}
                  onClick={() => handleDayClick(day)}
                  className={`border-b border-r border-gray-100 h-20 p-1 cursor-pointer hover:bg-pink-50 transition
                    ${selectedDate === dateStr ? 'bg-pink-50' : ''}
                    ${isToday(day) ? 'bg-pink-50' : ''}`}
                >
                  <div className={`text-xs font-semibold mb-1 w-6 h-6 flex items-center justify-center rounded-full
                    ${isToday(day) ? 'bg-pink-600 text-white' : 'text-gray-700'}`}>
                    {day}
                  </div>
                  <div className="space-y-0.5 overflow-hidden">
                    {dayEvents.slice(0, 2).map(ev => (
                      <div key={ev.id}
                        className="text-xs px-1 rounded truncate text-white font-medium"
                        style={{ backgroundColor: ev.color || '#60a5fa' }}>
                        {ev.title}
                      </div>
                    ))}
                    {dayEvents.length > 2 && (
                      <div className="text-xs text-gray-400">+{dayEvents.length - 2} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Selected day events */}
        {selectedDate && selectedEvents.length > 0 && (
          <div className="bg-white rounded-2xl shadow p-4">
            <h3 className="font-semibold text-gray-700 mb-3">
              Events on {new Date(selectedDate + 'T00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </h3>
            <div className="space-y-2">
              {selectedEvents.map(ev => (
                <div key={ev.id} className="flex items-center justify-between p-3 rounded-xl"
                  style={{ backgroundColor: (ev.color || '#60a5fa') + '20' }}>
                  <div>
                    <p className="font-medium text-gray-800">{ev.title}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(ev.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} –{' '}
                      {new Date(ev.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {ev.repeat !== 'NONE' && ` · Repeats ${ev.repeat.toLowerCase()}`}
                    </p>
                  </div>
                  <button onClick={() => handleDelete(ev.id)}
                    className="text-red-400 hover:text-red-600 text-sm ml-3">🗑️</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Schedule;
